/**
 * Lost Reason Modal
 */
import React, { useState } from "react";
import { Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const LOST_REASONS = [
  { value: "price_too_high", label: "Price Too High", description: "Budget didn't match our pricing" },
  { value: "chose_competitor", label: "Chose Competitor", description: "Went with another contractor" },
  { value: "ghosted", label: "Ghosted", description: "No response after multiple attempts" },
  { value: "project_cancelled", label: "Project Cancelled", description: "They cancelled or postponed" },
  { value: "bad_fit", label: "Bad Fit", description: "Not a good fit for our services" },
  { value: "other", label: "Other", description: "Other reason" },
];

const LostReasonModal = ({ isOpen, contact, onClose, onSubmit }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!selectedReason) return;
    onSubmit(selectedReason, notes);
    setSelectedReason("");
    setNotes("");
  };

  const handleClose = () => {
    setSelectedReason("");
    setNotes("");
    onClose();
  };

  const fullName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : "";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Archive className="h-5 w-5" />
            Mark as Lost
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-slate-400 text-sm mb-4">
            Why did we lose <span className="text-white font-medium">{fullName}</span>?
          </p>

          {/* Reason Selection */}
          <div className="space-y-2 mb-4">
            {LOST_REASONS.map(reason => (
              <button
                key={reason.value}
                onClick={() => setSelectedReason(reason.value)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedReason === reason.value
                    ? "border-red-500 bg-red-500/10"
                    : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                }`}
              >
                <p className="font-medium text-slate-200">{reason.label}</p>
                <p className="text-xs text-slate-500">{reason.description}</p>
              </button>
            ))}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-slate-400">Additional Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="border-slate-700">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedReason}
            className="bg-red-600 hover:bg-red-500"
          >
            <Archive className="h-4 w-4 mr-2" />
            Mark as Lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LostReasonModal;
